import { observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';

import { HotelMapModel, MapMarker } from 'models/hotel/map-types';
import { MarkerIcon } from 'src/common/models/hotel/map-types/map-marker';

import { AirportMarker, CircleMarker, HotelMarker, PlaceMarker, RestaurantMarker, TransitMarker } from 'uitk-react-map';

export interface MapMarkersProps {
  analytics?: AnalyticsStore;
  google?: any;
  map?: HotelMapModel;
  markers: MapMarker[];
  onMapClick?: () => void;
  onSelect?: (pin: MapMarker) => void;
  trackMarkerSelect?: (icon: MarkerIcon) => void;
}

const MarkerInstance = {
  HOTEL: HotelMarker,
  LANDMARK: PlaceMarker,
  AIRPORT: AirportMarker,
  RESTAURANT: RestaurantMarker,
  TRAIN: TransitMarker,
  METRO: TransitMarker,
  CIRCLE: CircleMarker
};

@observer
export class MapMarkers extends React.Component<MapMarkersProps> {
  public constructor(props: MapMarkersProps) {
    super(props);
  }

  public render() {
    const { google, map, markers } = this.props;
    if (map && google) {
      return markers.map((pin, index) => {
        const markerProps = {
          pin,
          index,
          google,
          map,
          onMapClick: this.props.onMapClick,
          onSelect: this.props.onSelect,
          trackMarkerSelect: this.props.trackMarkerSelect
        };

        return <MarkerPin key={pin.position.lat.toString() + pin.position.lng.toString()} {...markerProps} />;
      });
    }

    return null;
  }
}

interface MarkerAttributes {
  pin: MapMarker;
  index: number;
  google: any;
  map: HotelMapModel;
}

export function createPin(marker, zIndex: number, google, map) {
  const pinProps = {
    google,
    map,
    position: marker.position,
    sheet: marker.sheet,
    zIndex
  };

  if (MarkerInstance[marker.icon]) {
    return new MarkerInstance[marker.icon]({ ...pinProps });
  }

  return null;
}

interface UseMapHookParams extends MarkerAttributes {
  handleClick: (pin) => () => void;
  onMapClick: (marker?) => void;
}

export function useMarker({ pin, index, handleClick, onMapClick, google, map }: UseMapHookParams) {
  React.useEffect(() => {
    const uitkPin = createPin(pin, index, google, map);

    if (!uitkPin) {
      return null;
    }

    uitkPin.marker.position = uitkPin.marker.getPosition();

    uitkPin.marker.addListener('click', handleClick(uitkPin));
    if (google.maps && google.maps.event && uitkPin.marker.anchor) {
      google.maps.event.addDomListener(uitkPin.marker.anchor, 'click', handleClick(uitkPin));
    }

    return () => {
      onMapClick(uitkPin);
      uitkPin.marker.setMap(null);
    };
  }, []);
}

interface MarkerPinProps extends MarkerAttributes {
  onSelect: (pin: MapMarker) => void;
  trackMarkerSelect: (icon) => void;
  onMapClick: (marker?: MapMarker) => void;
}

export function MarkerPin({ pin, google, onSelect, trackMarkerSelect, onMapClick, index, map }: MarkerPinProps) {
  if (pin === null) {
    return null;
  }

  const handleClick = utikPin => () => {
    if (utikPin.sheet) {
      onSelect(utikPin);
      google.maps.event.addDomListenerOnce(map, 'mouseup', () => {
        onMapClick();
        google.maps.event.clearListeners(map, 'mouseup');
      });
    }
    trackMarkerSelect(pin.icon);
  };

  useMarker({ pin, index, handleClick, onMapClick, google, map });

  return null;
}

MarkerPin.displayName = 'MarkerPin';
